<template>
  <v-container fluid>
    <v-card>
      <v-card-title>
        Gestión de Categorías
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar categoría..."
          single-line
          hide-details
          outlined
          dense
          clearable
          class="categories-search"
        ></v-text-field>
        <v-btn color="primary" dark class="ml-3" @click="openCreateDialog">
          <v-icon left>mdi-plus</v-icon>
          Nueva Categoría
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-alert v-if="error" type="error" dense>
          No se pudieron cargar las categorías. Intenta nuevamente.
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="categories"
          :search="search"
          :loading="loading"
          loading-text="Cargando categorías..."
          no-data-text="No hay categorías registradas"
          no-results-text="No se encontraron categorías que coincidan con la búsqueda"
          class="elevation-1"
        >
          <template #[`item.IMAGE`]="{ item }">
            <v-avatar size="36" tile>
              <v-img v-if="item.IMAGE" :src="item.IMAGE"></v-img>
              <v-icon v-else size="20" color="grey">mdi-image-off</v-icon>
            </v-avatar>
          </template>
          <template #[`item.NAME`]="{ item }">
            <span class="font-weight-medium">{{ item.NAME }}</span>
          </template>
          <template #[`item.parentName`]="{ item }">
            {{ item.parentName || '-' }}
          </template>
          <template #[`item.actions`]="{ item }">
            <v-icon small class="mr-2" @click="openEditDialog(item)">
              mdi-pencil
            </v-icon>
            <v-icon small color="red" @click="openDeleteDialog(item)">
              mdi-delete
            </v-icon>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialog" max-width="760px">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ isEditing ? 'Editar Categoría' : 'Nueva Categoría' }}</span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="editedItem.NAME"
                  label="Nombre"
                  :error-messages="nameErrors"
                  @input="clearNameErrors"
                  outlined
                  dense
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-autocomplete
                  v-model="editedItem.PARENTID"
                  :items="availableParentCategories"
                  item-text="NAME"
                  item-value="ID"
                  label="Categoría padre"
                  placeholder="Sin categoría padre"
                  clearable
                  outlined
                  dense
                  :hint="isEditing ? 'No puedes elegir la misma categoría como padre.' : ''"
                  persistent-hint
                ></v-autocomplete>
              </v-col>
              <v-col cols="12" md="4" class="text-center">
                <v-card outlined class="pa-2">
                  <div v-if="!previewImage" class="grey lighten-4 d-flex align-center justify-center" style="height: 170px;">
                    <v-icon size="56" color="grey lighten-1">mdi-image-off</v-icon>
                  </div>
                  <v-img
                    v-else
                    :src="previewImage"
                    height="170"
                    contain
                    class="grey lighten-4"
                  ></v-img>
                  <v-card-actions class="justify-center mt-2">
                    <v-btn small color="primary" @click="$refs.fileInput.click()">
                      <v-icon left>mdi-camera</v-icon> {{ previewImage ? 'Cambiar' : 'Subir' }}
                    </v-btn>
                    <v-btn small color="error" icon v-if="previewImage" @click="removeImage">
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </v-card-actions>
                  <input type="file" ref="fileInput" class="d-none" accept="image/*" @change="onFileSelected">
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDialog">Cancelar</v-btn>
          <v-btn color="primary" text :loading="saving" @click="saveCategory">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420px">
      <v-card>
        <v-card-title class="text-h6">Confirmar eliminación</v-card-title>
        <v-card-text>
          ¿Deseas eliminar la categoría "{{ itemToDelete ? itemToDelete.NAME : '' }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteDialog">Cancelar</v-btn>
          <v-btn color="red darken-1" text :loading="deleting" @click="confirmDelete">Eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cropperDialog" persistent max-width="760px">
      <v-card>
        <v-card-title class="text-h5">Recortar Imagen</v-card-title>
        <v-card-text>
          <vue-cropper
            ref="cropper"
            :src="cropperImg"
            alt="Source Image"
            :view-mode="1"
            drag-mode="move"
            :aspect-ratio="1"
            :auto-crop-area="1"
            :background="false"
          ></vue-cropper>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cropperDialog = false">Cancelar</v-btn>
          <v-btn color="primary" @click="cropImage">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import * as categoryService from '@/services/categoryService';
import VueCropper from 'vue-cropperjs';

export default {
  name: 'CategoryAdminView',
  components: { VueCropper },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      error: false,
      search: '',
      dialog: false,
      deleteDialog: false,
      editedIndex: -1,
      nameErrors: [],
      itemToDelete: null,
      categories: [],
      editedItem: {
        ID: null,
        NAME: '',
        PARENTID: null,
        IMAGE: null,
      },
      defaultItem: {
        ID: null,
        NAME: '',
        PARENTID: null,
        IMAGE: null,
      },
      previewImage: null,
      cropperDialog: false,
      cropperImg: '',
      headers: [
        { text: 'Imagen', value: 'IMAGE', sortable: false, width: '72px' },
        { text: 'ID', value: 'ID', sortable: true, width: '120px' },
        { text: 'Nombre', value: 'NAME', sortable: true },
        { text: 'Categoría padre', value: 'parentName', sortable: true },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' },
      ],
    };
  },
  computed: {
    isEditing() {
      return this.editedIndex !== -1;
    },
    availableParentCategories() {
      if (!this.isEditing || !this.editedItem.ID) {
        return this.categories;
      }
      return this.categories.filter(category => category.ID !== this.editedItem.ID);
    },
  },
  mounted() {
    this.fetchCategories();
  },
  methods: {
    clearNameErrors() {
      this.nameErrors = [];
    },
    async fetchCategories() {
      this.loading = true;
      this.error = false;
      try {
        const response = await categoryService.fetchCategories();
        this.categories = response.map(category => ({
          ...category,
          parentName: category.PARENT ? category.PARENT.NAME : '',
        }));
      } catch (error) {
        this.error = true;
        this.$emit('show-alert', { message: 'Error al cargar categorías.', type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    openCreateDialog() {
      this.editedIndex = -1;
      this.editedItem = { ...this.defaultItem };
      this.previewImage = null;
      this.nameErrors = [];
      this.dialog = true;
    },
    async openEditDialog(item) {
      this.editedIndex = this.categories.findIndex(category => category.ID === item.ID);
      this.saving = true;
      try {
        const category = await categoryService.getCategoryById(item.ID);
        this.editedItem = {
          ID: category.ID,
          NAME: category.NAME,
          PARENTID: category.PARENTID || null,
          IMAGE: category.IMAGE || null,
        };
        this.previewImage = category.IMAGE || null;
        this.nameErrors = [];
        this.dialog = true;
      } catch (error) {
        this.$emit('show-alert', {
          message: `Error al cargar categoría: ${error.response?.data?.mensaje || error.message}`,
          type: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
    closeDialog() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = { ...this.defaultItem };
        this.previewImage = null;
        this.cropperImg = '';
        this.cropperDialog = false;
        this.editedIndex = -1;
        this.nameErrors = [];
      });
    },
    async saveCategory() {
      const name = (this.editedItem.NAME || '').trim();
      if (!name) {
        this.nameErrors = ['El nombre es obligatorio.'];
        return;
      }
      if (this.isEditing && this.editedItem.PARENTID === this.editedItem.ID) {
        this.$emit('show-alert', {
          message: 'Una categoría no puede ser su propia categoría padre.',
          type: 'warning',
        });
        return;
      }

      this.saving = true;
      try {
        const payload = {
          NAME: name,
          PARENTID: this.editedItem.PARENTID || null,
          IMAGE: this.editedItem.IMAGE,
        };

        if (this.isEditing) {
          await categoryService.updateCategory(this.editedItem.ID, payload);
          this.$emit('show-alert', { message: 'Categoría actualizada con éxito.', type: 'success' });
        } else {
          await categoryService.createCategory(payload);
          this.$emit('show-alert', { message: 'Categoría creada con éxito.', type: 'success' });
        }

        this.closeDialog();
        await this.fetchCategories();
      } catch (error) {
        this.$emit('show-alert', {
          message: `Error al guardar categoría: ${error.response?.data?.mensaje || error.message}`,
          type: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
    openDeleteDialog(item) {
      this.itemToDelete = item;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.$nextTick(() => {
        this.itemToDelete = null;
      });
    },
    async confirmDelete() {
      if (!this.itemToDelete) return;

      this.deleting = true;
      try {
        await categoryService.deleteCategory(this.itemToDelete.ID);
        this.$emit('show-alert', { message: 'Categoría eliminada con éxito.', type: 'success' });
        this.closeDeleteDialog();
        await this.fetchCategories();
      } catch (error) {
        this.$emit('show-alert', {
          message: `Error al eliminar categoría: ${error.response?.data?.mensaje || error.message}`,
          type: 'error',
        });
      } finally {
        this.deleting = false;
      }
    },
    onFileSelected(e) {
      const file = e.target.files[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        this.$emit('show-alert', { message: 'La imagen es muy grande (Máx 5MB)', type: 'warning' });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        this.cropperImg = event.target.result;
        this.cropperDialog = true;
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    },
    cropImage() {
      this.previewImage = this.$refs.cropper.getCroppedCanvas({
        width: 100,
        height: 100,
        imageSmoothingQuality: 'high',
      }).toDataURL('image/jpeg', 0.8);

      this.editedItem.IMAGE = this.previewImage;
      this.cropperDialog = false;
    },
    removeImage() {
      this.previewImage = null;
      this.editedItem.IMAGE = null;
    },
  },
};
</script>

<style scoped>
.categories-search {
  max-width: 360px;
}
</style>
